import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { osceAPI, scenariosAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Pencil, 
  Trash,
  Clipboard,
  CalendarBlank,
  Clock,
  Users,
  Play,
  Spinner,
  WarningCircle,
  CheckCircle,
  Target,
  Stethoscope,
  ChatCircle,
  Flask,
  Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATION_TYPES = [
  { value: 'anamnesis', label: 'Anamnesis', icon: ChatCircle, color: 'bg-blue-100 text-blue-600' },
  { value: 'examen_fisico', label: 'Examen Físico', icon: Stethoscope, color: 'bg-green-100 text-green-600' },
  { value: 'procedimiento', label: 'Procedimiento', icon: Flask, color: 'bg-purple-100 text-purple-600' },
  { value: 'comunicacion', label: 'Comunicación', icon: ChatCircle, color: 'bg-yellow-100 text-yellow-600' },
  { value: 'interpretacion', label: 'Interpretación', icon: Eye, color: 'bg-orange-100 text-orange-600' },
];

const SESSION_STATUS = {
  programada: { label: 'Programada', color: 'bg-blue-100 text-blue-700' },
  en_curso: { label: 'En Curso', color: 'bg-yellow-100 text-yellow-700' },
  finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-slate-100 text-slate-600' },
};

export default function EvaluationsPage() {
  const navigate = useNavigate();
  const { user, hasPermission, isInstructor } = useAuth();
  const [activeTab, setActiveTab] = useState('exams');
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [showExamModal, setShowExamModal] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [saving, setSaving] = useState(false);

  const canCreate = hasPermission('evaluaciones', 'crear') || isInstructor;
  const canEdit = hasPermission('evaluaciones', 'editar') || isInstructor;

  const [examForm, setExamForm] = useState({
    nombre: '',
    descripcion: '',
    duracion_total_minutos: 120,
    estaciones: []
  });

  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    exam_id: '',
    fecha_programada: '',
    estudiantes_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [examsRes, sessionsRes, scenariosRes] = await Promise.all([
        osceAPI.getExams().catch(() => ({ data: [] })),
        osceAPI.getSessions().catch(() => ({ data: [] })),
        scenariosAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setExams(examsRes.data);
      setSessions(sessionsRes.data);
      setScenarios(scenariosRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExamModal = (exam = null) => {
    if (exam) {
      setEditingExam(exam);
      setExamForm({
        nombre: exam.nombre || '',
        descripcion: exam.descripcion || '',
        duracion_total_minutos: exam.duracion_total_minutos || 120,
        estaciones: exam.estaciones || []
      });
    } else {
      setEditingExam(null);
      setExamForm({
        nombre: '',
        descripcion: '',
        duracion_total_minutos: 120,
        estaciones: []
      });
    }
    setShowExamModal(true);
  };

  const handleAddStation = () => {
    setExamForm(prev => ({
      ...prev,
      estaciones: [...prev.estaciones, {
        id: `station-${Date.now()}`,
        nombre: `Estación ${prev.estaciones.length + 1}`,
        tipo: 'anamnesis',
        duracion_minutos: 8,
        instrucciones_estudiante: '',
        instrucciones_evaluador: '',
        criterios: []
      }]
    }));
  };

  const handleUpdateStation = (index, field, value) => {
    setExamForm(prev => ({
      ...prev,
      estaciones: prev.estaciones.map((s, i) => 
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const handleRemoveStation = (index) => {
    setExamForm(prev => ({
      ...prev,
      estaciones: prev.estaciones.filter((_, i) => i !== index)
    }));
  };

  const handleAddCriteria = (stationIndex) => {
    setExamForm(prev => ({
      ...prev,
      estaciones: prev.estaciones.map((s, i) => 
        i === stationIndex 
          ? { ...s, criterios: [...(s.criterios || []), { id: `crit-${Date.now()}`, descripcion: '', puntos_maximos: 1, es_critico: false }] }
          : s
      )
    }));
  };

  const handleSaveExam = async () => {
    if (!examForm.nombre) {
      toast.error('Ingrese el nombre del examen');
      return;
    }
    if (examForm.estaciones.length === 0) {
      toast.error('Agregue al menos una estación');
      return;
    }

    setSaving(true);
    try {
      if (editingExam) {
        await osceAPI.updateExam(editingExam.id, examForm);
        toast.success('Examen actualizado');
      } else {
        await osceAPI.createExam(examForm);
        toast.success('Examen creado');
      }
      setShowExamModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExam = async (exam) => {
    if (!window.confirm(`¿Eliminar examen "${exam.nombre}"?`)) return;
    try {
      await osceAPI.deleteExam(exam.id);
      toast.success('Examen eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleStartSession = async (session) => {
    try {
      await osceAPI.startSession(session.id);
      toast.success('Sesión iniciada');
      navigate(`/osce/session/${session.id}`);
    } catch (error) {
      toast.error('Error al iniciar sesión');
    }
  };

  const handleCreateSession = async () => {
    if (!sessionForm.exam_id) {
      toast.error('Seleccione un examen');
      return;
    }
    
    setSaving(true);
    try {
      const response = await osceAPI.createSession({
        exam_id: sessionForm.exam_id,
        fecha_programada: sessionForm.fecha_programada || new Date().toISOString(),
        estudiantes_ids: sessionForm.estudiantes_ids
      });
      toast.success('Sesión creada');
      setShowSessionModal(false);
      setSessionForm({ exam_id: '', fecha_programada: '', estudiantes_ids: [] });
      loadData();
      
      // Ask if want to start immediately
      if (window.confirm('¿Desea iniciar la sesión ahora?')) {
        handleStartSession(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear sesión');
    } finally {
      setSaving(false);
    }
  };

  const getStationTypeConfig = (type) => {
    return STATION_TYPES.find(t => t.value === type) || STATION_TYPES[0];
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="evaluations-title">
              Evaluaciones OSCE
            </h1>
            <p className="text-slate-500">
              Gestión de exámenes clínicos objetivos estructurados
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => handleOpenExamModal()} className="gap-2" data-testid="create-exam-btn">
              <Plus size={18} />
              Nuevo Examen OSCE
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Clipboard size={24} className="mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{exams.length}</p>
              <p className="text-xs text-slate-500">Exámenes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Target size={24} className="mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {exams.reduce((acc, e) => acc + (e.estaciones?.length || 0), 0)}
              </p>
              <p className="text-xs text-slate-500">Estaciones</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CalendarBlank size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {sessions.filter(s => s.status === 'programada').length}
              </p>
              <p className="text-xs text-slate-500">Programadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {sessions.filter(s => s.status === 'finalizada').length}
              </p>
              <p className="text-xs text-slate-500">Completadas</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="exams" className="gap-2">
              <Clipboard size={16} />
              Exámenes
            </TabsTrigger>
            <TabsTrigger value="sessions" className="gap-2">
              <CalendarBlank size={16} />
              Sesiones
            </TabsTrigger>
          </TabsList>

          {/* Exams Tab */}
          <TabsContent value="exams" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exams.map(exam => (
                <Card key={exam.id} className="hover:shadow-md transition-shadow" data-testid={`exam-card-${exam.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{exam.nombre}</CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {exam.descripcion || 'Sin descripción'}
                        </CardDescription>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenExamModal(exam)}>
                            <Pencil size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteExam(exam)}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {exam.duracion_total_minutos} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Target size={14} />
                        {exam.estaciones?.length || 0} estaciones
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {exam.estaciones?.slice(0, 3).map(station => {
                        const config = getStationTypeConfig(station.tipo);
                        return (
                          <Badge key={station.id} variant="secondary" className={`text-xs ${config.color}`}>
                            {config.label}
                          </Badge>
                        );
                      })}
                      {(exam.estaciones?.length || 0) > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{exam.estaciones.length - 3}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {exams.length === 0 && (
              <Card className="p-8 text-center">
                <Clipboard size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">No hay exámenes OSCE creados</p>
                {canCreate && (
                  <Button onClick={() => handleOpenExamModal()}>
                    <Plus size={18} className="mr-2" />
                    Crear primer examen
                  </Button>
                )}
              </Card>
            )}
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="mt-6">
            <div className="space-y-4">
              {sessions.length > 0 ? sessions.map(session => (
                <Card key={session.id} data-testid={`session-card-${session.id}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <CalendarBlank size={24} className="text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{exams.find(e => e.id === session.exam_id)?.nombre || 'Examen'}</h3>
                          <p className="text-sm text-slate-500">
                            {new Date(session.fecha_programada).toLocaleDateString('es-CL', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <Badge className={SESSION_STATUS[session.status]?.color}>
                            {SESSION_STATUS[session.status]?.label}
                          </Badge>
                          <p className="text-sm text-slate-500 mt-1">
                            <Users size={14} className="inline mr-1" />
                            {session.estudiantes_ids?.length || 0} estudiantes
                          </p>
                        </div>
                        {session.status === 'programada' && (
                          <Button 
                            className="gap-2"
                            onClick={() => handleStartSession(session)}
                            data-testid={`start-session-${session.id}`}
                          >
                            <Play size={16} weight="fill" />
                            Iniciar
                          </Button>
                        )}
                        {session.status === 'en_curso' && (
                          <Button 
                            className="gap-2"
                            onClick={() => navigate(`/osce/session/${session.id}`)}
                            data-testid={`continue-session-${session.id}`}
                          >
                            <Play size={16} weight="fill" />
                            Continuar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <Card className="p-8 text-center">
                  <CalendarBlank size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No hay sesiones programadas</p>
                  {canCreate && (
                    <Button onClick={() => setShowSessionModal(true)} className="mt-4">
                      <Plus size={18} className="mr-2" />
                      Programar Sesión
                    </Button>
                  )}
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Exam Modal */}
        <Dialog open={showExamModal} onOpenChange={setShowExamModal}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingExam ? 'Editar Examen OSCE' : 'Nuevo Examen OSCE'}</DialogTitle>
              <DialogDescription>
                Configure las estaciones y criterios de evaluación
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre del Examen *</Label>
                    <Input
                      id="nombre"
                      value={examForm.nombre}
                      onChange={(e) => setExamForm(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej: OSCE Medicina Interna 2024"
                      data-testid="exam-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duracion">Duración Total (minutos)</Label>
                    <Input
                      id="duracion"
                      type="number"
                      min="30"
                      value={examForm.duracion_total_minutos}
                      onChange={(e) => setExamForm(prev => ({ ...prev, duracion_total_minutos: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={examForm.descripcion}
                    onChange={(e) => setExamForm(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Descripción del examen..."
                    rows={2}
                  />
                </div>

                {/* Stations */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Estaciones</Label>
                    <Button variant="outline" size="sm" onClick={handleAddStation}>
                      <Plus size={16} className="mr-1" />
                      Agregar Estación
                    </Button>
                  </div>

                  {examForm.estaciones.map((station, idx) => {
                    const config = getStationTypeConfig(station.tipo);
                    const Icon = config.icon;
                    return (
                      <Card key={station.id} className="border-l-4" style={{ borderLeftColor: config.color.includes('blue') ? '#3B82F6' : config.color.includes('green') ? '#10B981' : '#8B5CF6' }}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg ${config.color}`}>
                                <Icon size={18} />
                              </div>
                              <Input
                                value={station.nombre}
                                onChange={(e) => handleUpdateStation(idx, 'nombre', e.target.value)}
                                className="font-medium border-0 p-0 h-auto focus-visible:ring-0"
                              />
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleRemoveStation(idx)}>
                              <Trash size={16} />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Tipo</Label>
                              <Select value={station.tipo} onValueChange={(v) => handleUpdateStation(idx, 'tipo', v)}>
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATION_TYPES.map(type => (
                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Duración (min)</Label>
                              <Input
                                type="number"
                                min="1"
                                value={station.duracion_minutos}
                                onChange={(e) => handleUpdateStation(idx, 'duracion_minutos', parseInt(e.target.value))}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Instrucciones al estudiante</Label>
                            <Textarea
                              value={station.instrucciones_estudiante}
                              onChange={(e) => handleUpdateStation(idx, 'instrucciones_estudiante', e.target.value)}
                              rows={2}
                              className="text-sm"
                            />
                          </div>
                          {/* Criteria preview */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-500">{station.criterios?.length || 0} criterios</span>
                            <Button variant="ghost" size="sm" className="text-xs" onClick={() => handleAddCriteria(idx)}>
                              <Plus size={12} className="mr-1" />
                              Criterio
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {examForm.estaciones.length === 0 && (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <Target size={32} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500 text-sm">No hay estaciones configuradas</p>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExamModal(false)}>Cancelar</Button>
              <Button onClick={handleSaveExam} disabled={saving} data-testid="save-exam-btn">
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Session Modal */}
        <Dialog open={showSessionModal} onOpenChange={setShowSessionModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nueva Sesión OSCE</DialogTitle>
              <DialogDescription>
                Programe una nueva sesión de evaluación
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="exam">Examen OSCE *</Label>
                <Select 
                  value={sessionForm.exam_id} 
                  onValueChange={(v) => setSessionForm(prev => ({ ...prev, exam_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione un examen" />
                  </SelectTrigger>
                  <SelectContent>
                    {exams.map(exam => (
                      <SelectItem key={exam.id} value={exam.id}>
                        {exam.nombre} ({exam.estaciones?.length || 0} estaciones)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fecha">Fecha y hora programada</Label>
                <Input
                  id="fecha"
                  type="datetime-local"
                  value={sessionForm.fecha_programada}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, fecha_programada: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSessionModal(false)}>Cancelar</Button>
              <Button onClick={handleCreateSession} disabled={saving}>
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Crear Sesión'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
